/**
 * @fileoverview Says "Filed to Sweet Horizon peas (Front bed) · Move" right after a photo is added and Grow was sure which plant
 * it shows (B160): one toast for each photo Grow filed by itself, drawn by the one FiledByGrowLine (the server's sentence, word
 * for word, and Move), the length a move's Undo toast stays (TRAY_MOVE_UNDO_TOAST_MS). Move dismisses the toast and opens the
 * one move sheet (askMovePhoto). Each photo leaves the tray's piles at once and the garden is read again, so the plant card
 * shows the photo that landed on it.
 *
 * It is a plain function, not a hook, and the toast and the sheet outlive the screen that said them: the Check and save sheet
 * closes by itself when its photos were all filed, and the toast still carries the result (the Photos page's strip keeps it
 * for photos.FILED_SHOW_HOURS).
 *
 * Where: useTrayGuesses, after POST /tray/guess answers `filed`.
 * API: none (the garden's plants are read again: GET /plants).
 * Settings: TRAY_MOVE_UNDO_TOAST_MS (config/tunables/garden), given by useTrayGuesses.
 * Words (en/es): tray.filed.move (FiledByGrowLine); the sentence is the server's.
 *
 * @module sayFiled
 */

import type { QueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/components/ui/use-toast';
import { FiledByGrowLine } from '@/shared/components/ui/FiledByGrowLine';
import { askMovePhoto } from '@/shared/lib/movePhoto';
import type { FiledPhoto } from '@/shared/api/tray';
import { trayPhotoFiled } from './useTray';

/** The plants list's cache key (every language and garden's copy are read again). */
const PLANTS_KEY = ['plants'] as const;

/**
 * Says one toast for each photo Grow filed by itself, and reads the garden again.
 *
 * @param queryClient - The app's query client.
 * @param filed - The photos the server filed, as POST /tray/guess answered them.
 * @param durationMs - How long each toast stays (TRAY_MOVE_UNDO_TOAST_MS, the length a move's Undo toast stays).
 */
export function sayFiled(queryClient: QueryClient, filed: FiledPhoto[], durationMs: number): void {
  for (const each of filed) {
    // It is no longer on the tray: gone from the piles at once, the tray then read again.
    trayPhotoFiled(queryClient, each.trayId);
    const raised = toast({
      duration: durationMs,
      description: (
        <FiledByGrowLine
          text={each.line}
          thumbUrl={each.thumbUrl}
          onMove={() => { raised.dismiss(); askMovePhoto({ plantObjId: each.plantObjId, photoIds: each.photoIds }); }}
        />
      ),
    });
  }
  // A photo is now on a plant: its card, and the beds' counts, are read again.
  void queryClient.invalidateQueries({ queryKey: PLANTS_KEY });
}
